/** Separately mounted, pure-read adapter. HomeScene wiring remains with I. */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CharacterPose } from "@/shared/contracts";
import { PetFigure } from "./PetFigure";
import { PetMotion, type PetMotionStatus } from "@/features/pet_motion/PetMotion";
import type { MotionEvent } from "@/features/pet_motion/player";
import type { MotionSet } from "@/features/pet_motion/manifest";
import { homeMotionPolicy, motionForFacing, type HomeFacing, type HomeMotionInput, type HomeMotionProof } from "./homeMotionPolicy";

export interface HomeMotionCapability {
  identityGeneration: string;
  petId: string;
  identityKey: string;
  allowPathMotion: boolean;
  reason: string;
  proof: HomeMotionProof;
}
export interface HomeMotionAdapterProps extends HomeMotionInput {
  muted?: boolean;
  said?: string | null;
  /** Current scene fact, e.g. petted/eating; no fabricated activity. */
  scenePose?: CharacterPose;
  onCapability: (capability: HomeMotionCapability) => void;
  onEvent?: (event: MotionEvent) => void;
}

function useEnvironment(reduced: boolean | undefined) {
  const [systemReduced, setSystemReduced] = useState(() => typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [hidden, setHidden] = useState(() => document.visibilityState === "hidden");
  useEffect(() => {
    const media = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    const updateMedia = () => setSystemReduced(media?.matches ?? false);
    const updateVisibility = () => setHidden(document.visibilityState === "hidden");
    media?.addEventListener?.("change", updateMedia);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => { media?.removeEventListener?.("change", updateMedia); document.removeEventListener("visibilitychange", updateVisibility); };
  }, []);
  return { reduced: reduced ?? systemReduced, hidden };
}

export function HomeMotionAdapter(props: HomeMotionAdapterProps) {
  const active = props.state?.active;
  const motion = props.motion;
  // Header, explicit generation and current character changes create a fresh load/failure round.
  // URL rotation/revision-only counters do not pretend to be new frame semantics.
  const roundKey = JSON.stringify([props.identityGeneration, props.pet.pet_id, active?.character_set_id, active?.revision,
    active?.pet_id, active?.reference_version, active?.style_version, active?.assets.find((a) => a.pose === "neutral_full")?.content_sha256,
    props.bodyRevision, props.presence, motion && { ...motion, clips: undefined }]);
  return <HomeMotionRound key={roundKey} {...props} />;
}

function HomeMotionRound(props: HomeMotionAdapterProps) {
  const environment = useEnvironment(props.reducedMotion);
  const previousGood = useRef<MotionSet | null>(null);
  const [playingFacing, setPlayingFacing] = useState<HomeFacing | null>(props.desiredFacing);
  const facing = props.desiredFacing ?? (props.mode !== "moving" ? playingFacing : null);
  const policy = useMemo(() => homeMotionPolicy({ ...props, desiredFacing: facing, reducedMotion: environment.reduced, paused: props.paused || environment.hidden }, previousGood.current),
    [props.motion, props.pet, props.state, props.bodyRevision, props.identityGeneration, props.presence, props.mode, facing, props.synthetic, props.paused, environment.reduced, environment.hidden]);
  previousGood.current = policy.motion; // null/invalid/permission loss clears, never retained as a partial reply.
  const signature = policy.prepared?.signature ?? "";
  const [failureRound, setFailureRound] = useState<{ signature: string; keys: readonly string[] }>({ signature, keys: [] });
  let failures = failureRound;
  if (failures.signature !== signature) {
    failures = { signature, keys: [] };
    setFailureRound(failures);
  }
  const triplet = playingFacing ? [`stand_to_walk_${playingFacing}`, `walk_loop_${playingFacing}`, `walk_to_stand_${playingFacing}`] : [];
  const gaitBroken = failures.keys.some((k) => triplet.includes(k));
  const switching = policy.canPlayWalk && facing !== playingFacing;
  const includeWalk = policy.canPlayWalk && !gaitBroken && !!playingFacing && policy.directions.includes(playingFacing);
  const chosen = useMemo(() => motionForFacing(policy, playingFacing, includeWalk), [policy.motion, playingFacing, includeWalk]);
  const playbackKey = `${signature}|${playingFacing}|${includeWalk}`;
  const sleeping = props.mode === "sleeping";
  const staticFact = sleeping || props.mode === "busy" || props.mode === "eating" || props.mode === "unknown";
  const mounted = policy.bodyVisible && !staticFact && !!chosen;
  // A static branch unmounts the player even when its identity/key is unchanged.
  // Invalidate readiness during render, before any layout capability callback;
  // object identity also rejects statuses/events saved by an earlier instance.
  const [playbackRound, setPlaybackRound] = useState(() => ({ key: playbackKey, mounted }));
  let currentPlayback = playbackRound;
  if (currentPlayback.key !== playbackKey || currentPlayback.mounted !== mounted) {
    currentPlayback = { key: playbackKey, mounted };
    setPlaybackRound(currentPlayback);
  }
  const [observed, setObserved] = useState<{ round: typeof playbackRound; status: PetMotionStatus } | null>(null);
  const latest = useRef({ props, playbackRound: currentPlayback });
  latest.current = { props, playbackRound: currentPlayback };
  const alive = useRef(true);
  useLayoutEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const [wake, setWake] = useState(false);
  const previousMode = useRef(props.mode);
  useEffect(() => {
    if (previousMode.current === "sleeping" && props.mode !== "sleeping") setWake(true);
    if (props.mode === "sleeping") setWake(false);
    previousMode.current = props.mode;
  }, [props.mode]);

  const status = mounted && observed?.round === currentPlayback ? observed.status : null;
  const target = includeWalk && props.mode === "moving" && !switching ? "WALK" : "STAND";
  const necessary = ["stand_idle", ...triplet];
  // Semantic digests intentionally exclude signed URLs. Match the current required
  // URLs during render, rather than waiting for a passive status to revoke old keys.
  // Existing decoded URLs remain cached without inventing a new pet/generation.
  const loaded = !!status && necessary.every((key) => {
    const clip = chosen?.clips.find((c) => c.clip_key === key);
    return !!clip && status.loadedClipKeys.includes(key) && clip.frames.every((f) => status.loadedFrameUrls.includes(f.url));
  }) && !status.failed;
  let reason = policy.reason;
  if (gaitBroken) reason = "direction_frame_failed";
  else if (switching) reason = "direction_neutral_restart";
  else if (status?.failed || status?.brokenClipKeys.includes("stand_idle")) reason = "stand_frame_failed";
  else if (policy.canPlayWalk && !loaded) reason = "frames_loading";
  else if (policy.canPlayWalk && props.mode !== "moving") reason = "stopped";
  else if (policy.canPlayWalk && status?.clipKey !== `walk_loop_${playingFacing}`) reason = "starting_or_stopping";
  else if (policy.canPlayWalk) reason = policy.proof === "fixture_control" ? "fixture_control_ready" : "loaded_metadata_ready";
  const allowPathMotion = reason === "loaded_metadata_ready" && policy.proof !== "fixture_control";
  const proof = policy.proof === "fixture_control" ? policy.proof : loaded ? "loaded_metadata" : policy.proof;
  useLayoutEffect(() => {
    props.onCapability({ identityGeneration: props.identityGeneration, petId: props.pet.pet_id, identityKey: policy.identityKey, allowPathMotion, reason, proof });
  }, [props.onCapability, props.identityGeneration, props.pet.pet_id, policy.identityKey, allowPathMotion, reason, proof]);
  useLayoutEffect(() => () => {
    latest.current.props.onCapability({ identityGeneration: latest.current.props.identityGeneration, petId: latest.current.props.pet.pet_id, identityKey: policy.identityKey, allowPathMotion: false, reason: "unmounted", proof: "none" });
  }, [policy.identityKey]);

  const onStatus = (next: PetMotionStatus) => {
    if (!alive.current || !currentPlayback.mounted || latest.current.playbackRound !== currentPlayback) return;
    const nextKeys = [...new Set([...failures.keys, ...next.brokenClipKeys])];
    if (nextKeys.length !== failures.keys.length) setFailureRound({ signature, keys: nextKeys });
    setObserved((old) => old?.round === currentPlayback && JSON.stringify(old.status) === JSON.stringify(next) ? old : { round: currentPlayback, status: next });
    if (switching && next.state === "STAND" && next.clipKey === "stand_idle") setPlayingFacing(facing);
    else if (policy.canPlayWalk && playingFacing === null) setPlayingFacing(facing);
  };
  const onEvent = (event: MotionEvent) => {
    const current = latest.current.props;
    if (!alive.current || !currentPlayback.mounted || latest.current.playbackRound !== currentPlayback || current.muted || current.paused || environment.reduced || document.visibilityState === "hidden") return;
    current.onEvent?.(event);
  };
  if (!policy.bodyVisible) return null;
  const safeState = props.state?.pet_id === props.pet.pet_id && props.state.active?.pet_id === props.pet.pet_id ? props.state : null;
  const pose = sleeping ? "sleeping" : props.mode === "eating" ? "eating" : props.scenePose === "walking" ? "neutral_full" : props.scenePose ?? "neutral_full";
  return <span style={{ display: "block", width: "100%", height: "100%" }} data-testid="home-motion-adapter" data-path-motion={allowPathMotion ? "allowed" : "stopped"} data-reason={reason} data-proof={proof}
    data-wake={wake ? "observed_stable_only" : "none"} data-facing={playingFacing ?? ""}>
    {staticFact || !chosen ? <PetFigure pet={props.pet} state={safeState} pose={pose} said={props.said ?? null} /> :
      <PetMotion pet={props.pet} state={safeState} bodyRevision={props.bodyRevision} motion={chosen} pose={pose} said={props.said}
        initialState="STAND" facing={playingFacing ?? "front"} target={target} resetKey={policy.identityKey} waitForLoad requiredClipKeys={includeWalk ? necessary : ["stand_idle"]}
        paused={props.paused || environment.hidden} reducedMotion={environment.reduced} muted={props.muted} onStatus={onStatus} onEvent={onEvent} />}
  </span>;
}
